import React, { useState, useEffect } from "react";
import { getUsers, getOrders } from "../../firebase/firestoreService";

const fmt     = (n)  => `MK ${Number(n || 0).toLocaleString()}`;
const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const STATUS_STYLE = {
  Pending:   { background: "#fff3e0", color: "#f57c00" },
  Confirmed: { background: "#e3f2fd", color: "#1565c0" },
  Shipped:   { background: "#f3e5f5", color: "#7b1fa2" },
  Delivered: { background: "#e8f5e9", color: "#2e7d32" },
  Cancelled: { background: "#fde8e8", color: "#c0392b" },
};

export default function CustomersSection() {
  const [customers,    setCustomers]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [expandedId,   setExpandedId]   = useState(null);
  const [search,       setSearch]       = useState("");
  useEffect(() => {
    const load = async () => {
      const [users, orders] = await Promise.all([getUsers(), getOrders()]);

      // Group orders by userId
      const ordersByUser = {};
      orders.forEach((o) => {
        const uid = o.userId || "__guest__";
        if (!ordersByUser[uid]) ordersByUser[uid] = [];
        ordersByUser[uid].push(o);
      });

      // Merge users with their orders
      const merged = users.map((u) => {
        const userOrders = (ordersByUser[u.id] || []).sort(
          (a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
        );
        const totalSpent  = userOrders.filter((o) => o.status !== "Cancelled").reduce((s, o) => s + (o.total || 0), 0);
        return { ...u, orders: userOrders, totalSpent };
      });

      // Sort by most recent order, then by signup date
      merged.sort((a, b) => {
        const aLast = a.orders[0]?.createdAt?.toMillis?.() || 0;
        const bLast = b.orders[0]?.createdAt?.toMillis?.() || 0;
        return bLast - aLast;
      });

      setCustomers(merged);
      setLoading(false);
    };
    load();
  }, []);

  const displayName = (c) =>
    [c.firstName, c.lastName].filter(Boolean).join(" ") || c.phone || c.email || "Unknown";

  const filtered = customers.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      displayName(c).toLowerCase().includes(q) ||
      (c.phone  || "").toLowerCase().includes(q) ||
      (c.email  || "").toLowerCase().includes(q) ||
      (c.referredBy || "").toLowerCase().includes(q)
    );
  });

  const totalCustomers = customers.length;
  const totalRevenue   = customers.reduce((s, c) => s + c.totalSpent, 0);
  const totalOrders    = customers.reduce((s, c) => s + c.orders.length, 0);
  const referred       = customers.filter((c) => c.referredBy).length;

  return (
    <div>
      <div className="dash-main__header">
        <h2 className="dash-main__title">Customers</h2>
        <input
          className="form-input"
          style={{ maxWidth: 240, marginLeft: "auto" }}
          placeholder="Search name, phone, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Stats */}
      <div className="stat-cards" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-card__label">Total Customers</div>
          <div className="stat-card__value">{totalCustomers}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Total Orders</div>
          <div className="stat-card__value">{totalOrders}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Total Revenue</div>
          <div className="stat-card__value" style={{ fontSize: 18 }}>{fmt(totalRevenue)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Via Referral</div>
          <div className="stat-card__value">{referred}</div>
        </div>
      </div>

      {loading ? (
        <p className="dash-empty">Loading customers…</p>
      ) : filtered.length === 0 ? (
        <p className="dash-empty">{search ? "No customers match your search." : "No customers yet."}</p>
      ) : (
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Contact</th>
                <th>Joined</th>
                <th>Referred By</th>
                <th>Orders</th>
                <th>Total Spent</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <React.Fragment key={c.id}>
                  <tr style={{ cursor: "pointer" }} onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}>
                    <td style={{ fontWeight: 600 }}>{displayName(c)}</td>
                    <td style={{ color: "#777", fontSize: 12 }}>
                      {c.phone && <div>{c.phone}</div>}
                      {c.email && <div>{c.email}</div>}
                    </td>
                    <td style={{ color: "#aaa", fontSize: 12 }}>{fmtDate(c.createdAt)}</td>
                    <td>
                      {c.referredBy
                        ? <span className="referrals__code-badge">{c.referredBy}</span>
                        : <span style={{ color: "#ccc", fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ fontWeight: 600 }}>{c.orders.length}</td>
                    <td style={{ fontWeight: 700 }}>{fmt(c.totalSpent)}</td>
                    <td style={{ color: "#aaa", fontSize: 12 }}>
                      {c.orders.length > 0 ? (expandedId === c.id ? "▲" : "▼") : ""}
                    </td>
                  </tr>

                  {expandedId === c.id && c.orders.length > 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: 0, background: "#fafaf9" }}>
                        <div style={{ padding: "14px 20px" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                              <tr style={{ borderBottom: "1px solid #eee" }}>
                                {["Order", "Date", "Items", "Total", "Status"].map((h) => (
                                  <th key={h} style={{ textAlign: "left", padding: "6px 10px 6px 0", fontWeight: 600, color: "#aaa", fontSize: 11, letterSpacing: 1 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {c.orders.map((order) => (
                                <tr key={order.id} style={{ borderBottom: "1px solid #f5f2ee" }}>
                                  <td style={{ padding: "10px 10px 10px 0", fontWeight: 600 }}>
                                    #{(order.chargeId || order.id).slice(0, 8).toUpperCase()}
                                  </td>
                                  <td style={{ padding: "10px", color: "#777" }}>{fmtDate(order.createdAt)}</td>
                                  <td style={{ padding: "10px", color: "#555", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {(order.items || [])
                                      .map((item) => `${item.name}${(item.qty || 1) > 1 ? ` ×${item.qty}` : ""}`)
                                      .join(", ") || "—"}
                                  </td>
                                  <td style={{ padding: "10px", fontWeight: 700 }}>{fmt(order.total)}</td>
                                  <td style={{ padding: "10px" }}>
                                    <span style={{
                                      ...(STATUS_STYLE[order.status] || STATUS_STYLE.Pending),
                                      fontSize: 10, fontWeight: 700, letterSpacing: 1,
                                      padding: "3px 8px", borderRadius: 20,
                                    }}>
                                      {order.status || "Pending"}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
